'use client';

import { useState, useEffect } from 'react';
import { Play, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MethodBadge } from './method-badge';
import { PathParamEditor, resolvePath, getParamDefaults } from './path-param-editor';
import type { ProviderEndpoint, HttpMethod, RequestConfig } from '@/lib/integrations/types';

interface RequestBuilderProps {
  selectedEndpoint?: ProviderEndpoint | null;
  onExecute: (config: RequestConfig) => Promise<void>;
  loading?: boolean;
}

const METHODS: HttpMethod[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

export function RequestBuilder({
  selectedEndpoint,
  onExecute,
  loading,
}: RequestBuilderProps) {
  const [method, setMethod] = useState<HttpMethod>('GET');
  const [endpoint, setEndpoint] = useState('');
  const [pathParams, setPathParams] = useState<Record<string, string>>({});
  const [body, setBody] = useState('');
  const [bodyError, setBodyError] = useState<string | null>(null);

  // Update from selected endpoint
  useEffect(() => {
    if (selectedEndpoint) {
      setMethod(selectedEndpoint.method);
      setEndpoint(selectedEndpoint.path);
      // Initialize path params with defaults from endpoint definition
      setPathParams(getParamDefaults(selectedEndpoint.pathParams));
      setBody('');
      setBodyError(null);
    }
  }, [selectedEndpoint]);

  const handleBodyChange = (value: string) => {
    setBody(value);
    if (value.trim()) {
      try {
        JSON.parse(value);
        setBodyError(null);
      } catch {
        setBodyError('Invalid JSON');
      }
    } else {
      setBodyError(null);
    }
  };

  const handleExecute = async () => {
    const resolvedEndpoint = resolvePath(endpoint, pathParams);
    await onExecute({
      method,
      endpoint: resolvedEndpoint,
      pathParams,
      queryParams: {},
      body: body.trim() || undefined,
    });
  };

  const showBodyEditor = ['POST', 'PUT', 'PATCH'].includes(method);
  const canExecute = endpoint.trim() && !bodyError && !loading;

  return (
    <div className="space-y-4">
      {/* Method and Endpoint */}
      <div className="flex gap-2">
        <Select value={method} onValueChange={v => setMethod(v as HttpMethod)}>
          <SelectTrigger className="w-28">
            <SelectValue>
              <MethodBadge method={method} size="sm" />
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {METHODS.map(m => (
              <SelectItem key={m} value={m}>
                <MethodBadge method={m} size="sm" />
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          value={endpoint}
          onChange={e => setEndpoint(e.target.value)}
          placeholder="/api/endpoint"
          className="flex-1 font-mono"
        />

        <Button
          onClick={handleExecute}
          disabled={!canExecute}
          className="shrink-0"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <Play className="h-4 w-4 mr-1" />
              Execute
            </>
          )}
        </Button>
      </div>

      {/* Path Parameters */}
      <PathParamEditor
        path={endpoint}
        params={pathParams}
        onChange={setPathParams}
        pathParamDefs={selectedEndpoint?.pathParams}
      />

      {/* Body Editor */}
      {showBodyEditor && (
        <div className="space-y-2">
          <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Request Body (JSON)
          </Label>
          <Textarea
            value={body}
            onChange={e => handleBodyChange(e.target.value)}
            placeholder='{"key": "value"}'
            className="font-mono text-sm min-h-32 resize-y"
          />
          {bodyError && (
            <p className="text-xs text-destructive">{bodyError}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default RequestBuilder;
